// `/reset-password` (coverage matrix row 95, "Forgot password" on the sign-in page): a public
// route, because the person cannot sign in.
// - without `?token=`: asks for the account's email and has Better Auth mail a reset link
//   (`request-password-reset`). The answer is the same whether or not the address has an
//   account, so the page says "if there is an account" and never more;
// - with `?token=` (the link in the email): choose a new password (`reset-password`). Every
//   session of the account ends, so the next step is to sign in. A used or expired token says so
//   and offers a new link.
// In the in-memory API nothing is mailed, so the sent state offers to open the link here.
import {
  Button,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  Notice,
  Spinner,
} from "@companyos/ui";
import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import type { Api } from "../api/client";
import { AuthFrame } from "../features/auth/auth-frame";
import { rootRoute } from "./__root";

type ResetSearch = { token?: string };

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "reset-password",
  validateSearch: (search: Record<string, unknown>): ResetSearch => ({
    ...(typeof search.token === "string" && search.token ? { token: search.token } : {}),
  }),
  component: ResetPasswordPage,
});

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function ResetPasswordPage() {
  const { api } = resetPasswordRoute.useRouteContext();
  const { token } = resetPasswordRoute.useSearch();
  return token ? <ChoosePassword api={api} token={token} /> : <RequestReset api={api} />;
}

function RequestReset({ api }: { api: Api }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!EMAIL.test(email.trim())) {
      setError("Enter the email address of your account.");
      return;
    }
    setState("sending");
    setError(null);
    try {
      await api.auth.requestPasswordReset(email.trim());
      setState("sent");
    } catch (e) {
      setState("idle");
      setError(e instanceof Error ? e.message : "The email could not be sent.");
    }
  }

  const demoToken = api.fake?.lastResetToken(email) ?? null;
  return (
    <AuthFrame
      title="Reset your password"
      description="We email a link that lets you choose a new one."
      footer={<Link to="/login">Back to sign in</Link>}
    >
      {state === "sent" ? (
        <div className="flex flex-col gap-3">
          <Notice tone="success" title="Check your email" role="status">
            If there is an account for {email.trim()}, a reset link is on its way. It works once and
            expires in an hour.
          </Notice>
          {api.fake && demoToken ? (
            <Notice
              tone="info"
              title="In-memory API"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    void navigate({ to: "/reset-password", search: { token: demoToken } })
                  }
                >
                  Open the link
                </Button>
              }
            >
              Nothing is mailed in the demo.
            </Notice>
          ) : null}
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <FieldGroup className="gap-4">
            <Field data-invalid={error ? true : undefined}>
              <FieldLabel htmlFor="reset-email">Email</FieldLabel>
              <Input
                id="reset-email"
                type="email"
                autoComplete="email"
                className="h-10"
                value={email}
                aria-invalid={error ? true : undefined}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            {error ? (
              <Notice tone="danger" role="alert">
                {error}
              </Notice>
            ) : null}
            <Button type="submit" size="lg" disabled={state === "sending"}>
              {state === "sending" ? <Spinner /> : null}
              Email me a reset link
            </Button>
          </FieldGroup>
        </form>
      )}
    </AuthFrame>
  );
}

function ChoosePassword({ api, token }: { api: Api; token: string }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [state, setState] = useState<"idle" | "saving" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const short = next.length < 8;
  const mismatch = next !== confirm;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (short || mismatch) return;
    setState("saving");
    setError(null);
    try {
      await api.auth.resetPassword({ token, newPassword: next });
      setState("done");
    } catch (e) {
      setState("idle");
      setError(e instanceof Error ? e.message : "The password was not reset.");
    }
  }

  if (state === "done")
    return (
      <AuthFrame title="Password changed" description="You were signed out everywhere.">
        <Notice tone="success" title="Your password is set" role="status">
          Sign in with the new password.
        </Notice>
        <Link to="/login" className="text-sm font-medium underline">
          Go to sign in
        </Link>
      </AuthFrame>
    );

  return (
    <AuthFrame title="Choose a new password" description="At least 8 characters.">
      <form onSubmit={onSubmit} noValidate>
        <FieldGroup className="gap-4">
          <Field data-invalid={(touched && short) || undefined}>
            <FieldLabel htmlFor="reset-new">New password</FieldLabel>
            <Input
              id="reset-new"
              type="password"
              autoComplete="new-password"
              className="h-10"
              value={next}
              aria-invalid={(touched && short) || undefined}
              onChange={(e) => setNext(e.target.value)}
            />
            {touched && short ? <FieldError>At least 8 characters.</FieldError> : null}
          </Field>
          <Field data-invalid={(touched && mismatch) || undefined}>
            <FieldLabel htmlFor="reset-confirm">Repeat the new password</FieldLabel>
            <Input
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              className="h-10"
              value={confirm}
              aria-invalid={(touched && mismatch) || undefined}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {touched && mismatch ? <FieldError>The passwords differ.</FieldError> : null}
          </Field>
          {error ? (
            <Notice tone="danger" role="alert">
              {error}{" "}
              <Link to="/reset-password" className="underline">
                Ask for a new link
              </Link>
            </Notice>
          ) : null}
          <Button type="submit" size="lg" disabled={state === "saving"}>
            {state === "saving" ? <Spinner /> : null}
            Set new password
          </Button>
        </FieldGroup>
      </form>
    </AuthFrame>
  );
}
